import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack, Close, Download } from '@mui/icons-material';
import { callDashboardRpc } from '../../../api/dashboard';
import { FAILURE_LABELS, rfqListTab } from '../../tracking/opsHealth';
import { CONFIDENCE_LABELS } from '../../tracking/pricingAccuracy';
import { formatClock, formatDateRange, formatSgd, ignoredFilterFallback, poLinesDrill, PURCHASE_EXCLUSION_LABELS, SOURCE_LABELS, toCsv, type DrillSpec, type PriceSource, type PurchaseExclusion, type RecordKind, type RecordsResult } from './ownerData';

type Row = Record<string, unknown>;

interface Column {
  key: string;
  label: string;
  align?: 'right';
  /** Hidden below this breakpoint; still in the CSV. */
  hideBelow?: 'sm' | 'md';
  render?: (row: Row) => React.ReactNode;
  /** Value for the CSV when it differs from the raw field. */
  csv?: (row: Row) => unknown;
  width?: number;
  /** Folded into another cell on screen; its own column only in the CSV. */
  csvOnly?: boolean;
}

const sgt = (iso: unknown, withTime = true): string => {
  if (!iso || typeof iso !== 'string') return '-';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00+08:00` : iso);
  return d.toLocaleString('en-SG', {
    timeZone: 'Asia/Singapore',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  });
};
const sgtCsv = (iso: unknown) => (typeof iso === 'string' ? sgt(iso) : '');
const text = (v: unknown) => (v == null || v === '' ? '-' : String(v));
const money = (v: unknown) => (v == null ? '-' : formatSgd(Number(v)));

const REASON_LABELS: Record<string, string> = {
  own_document: 'DBR’s own document',
  non_sgd: 'Not SGD',
  no_total: 'No total',
  no_date: 'No order date',
};

const MAIL_OUTCOME_LABELS: Record<string, string> = {
  read: 'Read as an RFQ',
  duplicate: 'Already read before',
  other: 'Not an RFQ',
  ...FAILURE_LABELS,
};

const rfqLink = (row: Row, tab: string = 'QUOTED') =>
  row.rfq_id ? (
    <Link component={RouterLink} to={`/?tab=${tab}&rfq=${row.rfq_id}`} underline="hover">
      #{String(row.rfq_id)}
    </Link>
  ) : (
    '-'
  );

const COLUMNS: Record<RecordKind, Column[]> = {
  rfqs: [
    { key: 'received_at', label: 'Received (SGT)', render: (r) => sgt(r.received_at), csv: (r) => sgtCsv(r.received_at), width: 150 },
    {
      key: 'subject',
      label: 'Subject',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.subject)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {text(r.customer_email)}
          </Typography>
        </Box>
      ),
    },
    { key: 'customer_email', label: 'From', hideBelow: 'md', csvOnly: true, csv: (r) => r.customer_email },
    { key: 'rfq_ref', label: 'Customer ref', hideBelow: 'md' },
    {
      key: 'replied_at',
      label: 'First reply (SGT)',
      render: (r) => (r.replied_at ? sgt(r.replied_at) : <Chip size="small" variant="outlined" label="No email reply" />),
      csv: (r) => sgtCsv(r.replied_at),
      width: 150,
    },
    { key: 'channel', label: 'Sent', hideBelow: 'sm', render: (r) => (r.channel === 'system' ? 'System' : r.channel === 'manual' ? 'By hand' : '-') },
    { key: 'clock_minutes', label: 'Wait (clock)', align: 'right', render: (r) => formatClock(r.clock_minutes as number | null) },
    { key: 'office', label: 'Office hours', hideBelow: 'md', render: (r) => (r.office ? 'Yes' : 'No') },
    { key: 'rfq_id', label: 'RFQ', hideBelow: 'sm', render: rfqLink },
  ],
  orders: [
    { key: 'order_date', label: 'Order date', render: (r) => sgt(r.order_date, false), width: 110 },
    {
      key: 'order_number',
      label: 'PO number',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {text(r.order_number ?? r.order_key)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {text(r.counterparty_name)}
          </Typography>
        </Box>
      ),
    },
    { key: 'counterparty_name', label: 'Customer', hideBelow: 'md', csvOnly: true, csv: (r) => r.counterparty_name },
    { key: 'customer_key', label: 'Customer key', hideBelow: 'md' },
    { key: 'lines', label: 'Lines', align: 'right', hideBelow: 'sm' },
    { key: 'total', label: 'Total', align: 'right', render: (r) => money(r.total) },
    { key: 'subject', label: 'Email subject', hideBelow: 'md', render: (r) => <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{text(r.subject)}</Typography> },
    { key: 'attachment', label: 'Attachment / folder', hideBelow: 'md', render: (r) => [r.attachment, r.folder].filter(Boolean).join(' · ') || '-' },
  ],
  order_lines: [
    { key: 'order_date', label: 'Order date', render: (r) => sgt(r.order_date, false), width: 110 },
    { key: 'order_number', label: 'PO number', render: (r) => <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{text(r.order_number ?? r.order_key)}</Typography> },
    {
      key: 'item_name',
      label: 'Item',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.item_name)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[r.model_number, r.category].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      ),
    },
    { key: 'quantity', label: 'Qty', align: 'right', hideBelow: 'sm' },
    { key: 'unit_price', label: 'Unit', align: 'right', hideBelow: 'sm', render: (r) => (r.unit_price == null ? '-' : Number(r.unit_price).toLocaleString('en-SG')) },
    { key: 'total_price', label: 'Total', align: 'right', render: (r) => (r.total_price == null ? '-' : `${Number(r.total_price).toLocaleString('en-SG', { minimumFractionDigits: 2 })} ${r.currency ?? ''}`) },
    { key: 'counterparty_name', label: 'Customer', hideBelow: 'md' },
    { key: 'subject', label: 'Email subject', hideBelow: 'md', render: (r) => <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{text(r.subject)}</Typography> },
    { key: 'reason', label: 'Not counted because', hideBelow: 'sm', render: (r) => (r.reason ? REASON_LABELS[String(r.reason)] ?? String(r.reason) : 'Counted') },
  ],
  purchases: [
    { key: 'order_date', label: 'Date', render: (r) => sgt(r.order_date, false), width: 110 },
    {
      key: 'order_number',
      label: 'Document',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {text(r.order_number ?? r.purchase_key)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {text(r.supplier)}
          </Typography>
        </Box>
      ),
    },
    { key: 'supplier', label: 'Supplier', hideBelow: 'md', csvOnly: true, csv: (r) => r.supplier },
    { key: 'lines', label: 'Lines', align: 'right', hideBelow: 'sm' },
    { key: 'total', label: 'Total', align: 'right', render: (r) => (r.total == null ? '-' : `${Number(r.total).toLocaleString('en-SG', { minimumFractionDigits: 2 })} ${r.currency ?? ''}`) },
    { key: 'subject', label: 'Email subject', hideBelow: 'md', render: (r) => <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{text(r.subject)}</Typography> },
    {
      key: 'excluded_reason',
      label: 'Counted as cost',
      hideBelow: 'sm',
      render: (r) => (r.excluded_reason ? `No - ${PURCHASE_EXCLUSION_LABELS[r.excluded_reason as PurchaseExclusion] ?? r.excluded_reason}` : 'Yes'),
    },
    { key: 'items', label: 'Items', hideBelow: 'md', render: (r) => (Array.isArray(r.items) ? (r.items as Row[]).map((i) => text(i.item_name)).join('; ').slice(0, 160) : '-'), csv: (r) => (Array.isArray(r.items) ? (r.items as Row[]).map((i) => `${i.quantity ?? ''} x ${i.item_name ?? ''} @ ${i.unit_price ?? ''}`).join('; ') : '') },
  ],
  pricing_lines: [
    { key: 'quote_sent_at', label: 'Quote sent (SGT)', render: (r) => sgt(r.quote_sent_at), csv: (r) => sgtCsv(r.quote_sent_at), width: 150 },
    {
      key: 'item_name',
      label: 'Item',
      width: 240,
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.item_name)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {text(r.original_subject)}
          </Typography>
        </Box>
      ),
    },
    { key: 'original_subject', label: 'RFQ subject', hideBelow: 'md', csvOnly: true, csv: (r) => r.original_subject },
    { key: 'client_email', label: 'Customer', hideBelow: 'md' },
    { key: 'suggested', label: 'System price', align: 'right', render: (r) => money(r.suggested) },
    { key: 'sent', label: 'Price sent', align: 'right', render: (r) => money(r.sent) },
    { key: 'gap_pct', label: 'Gap', align: 'right', hideBelow: 'sm', render: (r) => (r.gap_pct == null ? '-' : `${r.gap_pct}%`) },
    // within is null on a line sent with no system price (pricing_lines {priced:false}).
    { key: 'within', label: 'Within tolerance', hideBelow: 'sm', render: (r) => (r.within == null ? '-' : r.within ? 'Yes' : 'No') },
    { key: 'source', label: 'Price source', hideBelow: 'md', render: (r) => SOURCE_LABELS[r.source as PriceSource] ?? text(r.source) },
    { key: 'confidence', label: 'Confidence', hideBelow: 'md', render: (r) => CONFIDENCE_LABELS[r.confidence as keyof typeof CONFIDENCE_LABELS] ?? text(r.confidence) },
    { key: 'rfq_id', label: 'RFQ', hideBelow: 'sm', render: rfqLink },
  ],
  system_quotes: [
    { key: 'sent_at', label: 'Sent (SGT)', render: (r) => sgt(r.sent_at), csv: (r) => sgtCsv(r.sent_at), width: 150 },
    {
      key: 'subject',
      label: 'Subject',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.subject)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {text(r.client_email)}
          </Typography>
        </Box>
      ),
    },
    { key: 'client_email', label: 'Customer', hideBelow: 'md', csvOnly: true, csv: (r) => r.client_email },
    { key: 'received_at', label: 'Received (SGT)', hideBelow: 'sm', render: (r) => sgt(r.received_at), csv: (r) => sgtCsv(r.received_at) },
    { key: 'clock_minutes', label: 'Wait (clock)', align: 'right', render: (r) => formatClock(r.clock_minutes as number | null) },
    { key: 'sent_by', label: 'Sent by', hideBelow: 'md' },
    { key: 'rfq_id', label: 'RFQ', hideBelow: 'sm', render: rfqLink },
  ],
  system_rfqs: [
    { key: 'received_at', label: 'Received (SGT)', render: (r) => sgt(r.received_at), csv: (r) => sgtCsv(r.received_at), width: 150 },
    {
      key: 'subject',
      label: 'Subject',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.subject)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {text(r.client_name)} · {text(r.client_email)}
          </Typography>
        </Box>
      ),
    },
    { key: 'client_name', label: 'Customer', csvOnly: true },
    { key: 'client_email', label: 'Customer email', csvOnly: true },
    { key: 'item_count', label: 'Items', align: 'right', hideBelow: 'sm' },
    { key: 'status', label: 'Status', hideBelow: 'sm' },
    { key: 'rfq_id', label: 'RFQ', render: (r) => rfqLink(r, rfqListTab(r.status as string | null)) },
  ],
  // Tracking History drills (20261027140000_dashboard_reports.sql).
  mail_events: [
    { key: 'last_at', label: 'Last seen (SGT)', render: (r) => sgt(r.last_at), csv: (r) => sgtCsv(r.last_at), width: 150 },
    {
      key: 'subject',
      label: 'Email',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.subject)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {r.type === 'EXCEL' ? 'Excel fill' : text(r.sender)}
          </Typography>
        </Box>
      ),
    },
    { key: 'sender', label: 'Sender', csvOnly: true },
    { key: 'outcome', label: 'Outcome', render: (r) => MAIL_OUTCOME_LABELS[String(r.outcome)] ?? text(r.outcome) },
    { key: 'attempts', label: 'Attempts', align: 'right', hideBelow: 'sm' },
    { key: 'error', label: 'Detail', hideBelow: 'md', render: (r) => <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{text(r.error)}</Typography> },
    { key: 'processed_at', label: 'First seen (SGT)', csvOnly: true, csv: (r) => sgtCsv(r.processed_at) },
    {
      key: 'rfq_id',
      label: 'RFQ',
      hideBelow: 'sm',
      // rfq_status (20261027160000) opens the list tab the RFQ is in now; without it, the Quoted tab as before.
      render: (r) =>
        r.rfq_id == null ? '-' : r.rfq_live ? rfqLink(r, typeof r.rfq_status === 'string' ? rfqListTab(r.rfq_status) : 'QUOTED') : `#${String(r.rfq_id)} (deleted)`,
      csv: (r) => (r.rfq_id == null ? '' : `${r.rfq_id}${r.rfq_live ? '' : ' (deleted)'}`),
    },
  ],
  deleted_rfqs: [
    { key: 'deleted_at', label: 'Deleted (SGT)', render: (r) => sgt(r.deleted_at), csv: (r) => sgtCsv(r.deleted_at), width: 150 },
    {
      key: 'subject',
      label: 'RFQ',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {text(r.subject)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            #{String(r.rfq_id)} · {[r.client_name, r.client_email].filter(Boolean).join(' · ') || '-'}
          </Typography>
        </Box>
      ),
    },
    { key: 'rfq_id', label: 'RFQ number', csvOnly: true },
    { key: 'client_name', label: 'Customer', csvOnly: true },
    { key: 'client_email', label: 'Customer email', csvOnly: true },
    { key: 'received_at', label: 'Received (SGT)', hideBelow: 'sm', render: (r) => sgt(r.received_at), csv: (r) => sgtCsv(r.received_at) },
    { key: 'status', label: 'Status when deleted', hideBelow: 'sm' },
    { key: 'quote_sent_at', label: 'Quote sent (SGT)', hideBelow: 'md', render: (r) => (r.quote_sent_at ? sgt(r.quote_sent_at) : 'Not sent'), csv: (r) => sgtCsv(r.quote_sent_at) },
    { key: 'auto_archived', label: 'Deleted by', render: (r) => (r.auto_archived ? 'Nightly archive job' : 'A person') },
  ],
};

const FALLBACK_NOTE = 'The exact list behind the figure you selected is not available yet (a database update is pending), so this is the closest list.';

interface Props {
  spec: DrillSpec | null;
  onClose: () => void;
}

/**
 * Lists the records behind a number, newest first, up to 500 at a time, with
 * a CSV of what is shown. A purchase order row opens its lines.
 */
const RecordsDialog: React.FC<Props> = ({ spec, onClose }) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [stack, setStack] = useState<DrillSpec[]>([]);
  const [result, setResult] = useState<RecordsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const current = stack[stack.length - 1] ?? null;

  useEffect(() => {
    setStack(spec ? [spec] : []);
  }, [spec]);

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    callDashboardRpc('get_owner_records', { p_kind: current.kind, p_from: current.from, p_to: current.to, p_filter: current.filter ?? {} })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        const records = data as unknown as RecordsResult | null;
        // The database ignored a newer filter: show the closest list it can serve instead.
        const fallback = !rpcError && records ? ignoredFilterFallback(current, records.total) : null;
        if (fallback) {
          setStack((s) => [...s.slice(0, -1), { ...fallback, note: `${fallback.note ? `${fallback.note} ` : ''}${FALLBACK_NOTE}` }]);
          return;
        }
        if (rpcError) setError(rpcError.message);
        else setResult(records);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  const columns = current ? COLUMNS[current.kind] : [];
  const visible = columns.filter((c) => !c.csvOnly);

  const download = () => {
    if (!current || !result) return;
    const rows = result.rows.map((r) => Object.fromEntries(columns.map((c) => [c.key, c.csv ? c.csv(r) : r[c.key]])));
    const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${current.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openRow = (row: Row) => {
    if (!current) return;
    if (current.kind === 'orders' && row.order_key) {
      setStack((s) => [...s, poLinesDrill(current, row)]);
    }
  };

  const hide = (c: Column) => (c.hideBelow ? { display: { xs: 'none', [c.hideBelow]: 'table-cell' } } : {});

  return (
    <Dialog open={!!spec} onClose={onClose} fullScreen={phone} maxWidth="lg" fullWidth aria-labelledby="records-title">
      <DialogTitle id="records-title" sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {stack.length > 1 && (
            <IconButton size="small" aria-label="Back" onClick={() => setStack((s) => s.slice(0, -1))}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="h6" component="span">
              {current?.title}
            </Typography>
            {current && (
              <Typography variant="body2" color="text.secondary">
                {formatDateRange(current.from, current.to)} (Singapore dates)
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 1, sm: 3 } }}>
        {current?.note && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {current.note}
          </Typography>
        )}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => setStack((s) => [...s.slice(0, -1), { ...s[s.length - 1] }])}>
                Retry
              </Button>
            }
          >
            Could not load the records: {error}
          </Alert>
        )}
        {result && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="body2">
                {result.total === 0
                  ? 'No records.'
                  : result.total > result.rows.length
                    ? `Showing the newest ${result.rows.length.toLocaleString('en-SG')} of ${result.total.toLocaleString('en-SG')} records.`
                    : `${result.total.toLocaleString('en-SG')} record${result.total === 1 ? '' : 's'}.`}
                {result.sum != null && ` Total ${formatSgd(Number(result.sum))}.`}
                {current?.kind === 'orders' && result.total > 0 && ' Select a PO to see its lines.'}
              </Typography>
              {result.rows.length > 0 && (
                <Button size="small" startIcon={<Download />} onClick={download}>
                  Download CSV
                </Button>
              )}
            </Stack>
            {result.rows.length > 0 && phone && (
              // Phone: one card per record with every on-screen field labelled,
              // instead of a table squeezed to 360px.
              <Stack spacing={1}>
                {result.rows.map((row, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    onClick={current?.kind === 'orders' ? () => openRow(row) : undefined}
                    sx={{ p: 1.5, ...(current?.kind === 'orders' ? { cursor: 'pointer' } : {}) }}
                  >
                    {visible.map((c) => (
                      <Box key={c.key} sx={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 1, py: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          {c.label}
                        </Typography>
                        <Box sx={{ fontSize: 14, minWidth: 0, wordBreak: 'break-word' }}>{c.render ? c.render(row) : text(row[c.key])}</Box>
                      </Box>
                    ))}
                  </Paper>
                ))}
              </Stack>
            )}
            {result.rows.length > 0 && !phone && (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {visible.map((c) => (
                        <TableCell key={c.key} align={c.align} sx={{ whiteSpace: 'nowrap', fontWeight: 600, ...hide(c), ...(c.width ? { minWidth: c.width } : {}) }}>
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.map((row, i) => (
                      <TableRow
                        key={i}
                        hover
                        onClick={current?.kind === 'orders' ? () => openRow(row) : undefined}
                        sx={current?.kind === 'orders' ? { cursor: 'pointer' } : undefined}
                      >
                        {visible.map((c) => (
                          <TableCell key={c.key} align={c.align} sx={{ verticalAlign: 'top', ...hide(c), ...(c.align ? { whiteSpace: 'nowrap' } : {}) }}>
                            {c.render ? c.render(row) : text(row[c.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecordsDialog;
